import { useEffect, useState } from "react";

import { runningAsApp } from "@/lib/runningAsApp";

const DISMISSED_KEY = "yarn-install-hint-dismissed";

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
}

let waitingInstall: InstallEvent | null = null;

/** Android offers this once, early, so it has to be caught before any page is showing. */
export function listenForInstallOffer(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    waitingInstall = event as InstallEvent;
  });
}

const isIphone = (): boolean => /iPhone|iPod/.test(navigator.userAgent);

/** A phone: the browser says so, or a touch screen too small for a desktop or tablet. */
export function isPhone(): boolean {
  if (/iPhone|iPod|Android.+Mobile/.test(navigator.userAgent)) {
    return true;
  }
  const hints = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (hints?.mobile !== undefined) {
    return hints.mobile;
  }
  const touch = window.matchMedia("(pointer: coarse)").matches;
  return touch && Math.min(window.screen.width, window.screen.height) < 600;
}

function dismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    return true;
  }
}

/** Android gets a real install button. iPhone can only be told how, so it is told once. */
export function InstallHint() {
  // Desktop browsers offer to install too, but the tip is only worth it on a phone.
  const [hidden, setHidden] = useState(() => !isPhone() || runningAsApp() || dismissed());
  const [offer, setOffer] = useState(waitingInstall);

  useEffect(() => {
    const onOffer = (): void => setOffer(waitingInstall);
    window.addEventListener("beforeinstallprompt", onOffer);
    return () => window.removeEventListener("beforeinstallprompt", onOffer);
  }, []);

  if (hidden || (offer === null && !isIphone())) {
    return null;
  }
  const close = (): void => {
    setHidden(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // It may show again next time, which does no harm.
    }
  };
  return (
    <aside className="install-hint" aria-label="Install Yarn">
      {offer === null ? (
        <span>Add Yarn to your home screen: tap Share, then Add to Home Screen.</span>
      ) : (
        <button
          type="button"
          className="install-hint__install"
          onClick={() => {
            void offer.prompt();
            close();
          }}
        >
          Install Yarn
        </button>
      )}
      <button type="button" className="install-hint__close" aria-label="Not now" onClick={close}>
        ×
      </button>
    </aside>
  );
}
